import './shared/styles/admin-form-page.scss';
import './shared/styles/admin-global.scss';
import './shared/styles/admin-page.scss';

import { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, type RouteObject, useLocation } from 'react-router';

import { ADMIN_ROUTES, ROUTES, getFullAdminRoute } from '@/shared/routes';
import { MainLoader } from '@/shared/ui';
import { useAppSelector } from '@/store';

const Users = lazy(() => import('./pages/Users/Users'));
const PositionInfoPage = lazy(
  () => import('./pages/Users/PositionInfoPage/PositionInfoPage')
);
const RulesActionsInfoPage = lazy(
  () => import('./pages/Users/RulesActionsInfoPage/RulesActionsInfoPage')
);
const UserInfoPage = lazy(
  () => import('./pages/Users/UserInfoPage/UserInfoPage')
);

const Clients = lazy(() => import('./pages/Clients/Clients'));
const ClientInfoPage = lazy(
  () => import('./pages/Clients/ClientInfoPage/ClientInfoPage')
);

const Branches = lazy(() => import('./pages/Branches/Branches'));
const BranchInfo = lazy(() => import('./pages/Branches/BranchInfo/BranchInfo'));

const BlackList = lazy(() => import('./pages/BlackList/BlackList'));
const BlackItem = lazy(() => import('./pages/BlackList/BlackItem/BlackItem'));

const Modules = lazy(() => import('./pages/Modules/Modules'));
const ModuleInfoPage = lazy(
  () => import('./pages/Modules/ModuleInfo/ModuleInfo')
);

const ExternalServices = lazy(
  () => import('./pages/ExternalServices/ExternalServices')
);
const ExternalServiceInfo = lazy(
  () =>
    import('./pages/ExternalServices/ExternalServiceInfo/ExternalServiceInfo')
);

const Products = lazy(() => import('./pages/Products/Products'));
const ProductInfo = lazy(
  () => import('./pages/Products/ProductInfo/ProductInfo')
);

const Partners = lazy(() => import('./pages/Partners/Partners'));
const PartnerInfo = lazy(
  () => import('./pages/Partners/PartnerInfo/PartnerInfo')
);
const PartnerDealerInfo = lazy(
  () => import('./pages/Partners/PartnerDealerInfo/PartnerDealerInfo')
);
const AssessmentCenterInfo = lazy(
  () => import('./pages/Partners/AssessmentCenterInfo/AssessmentCenterInfo')
);

const Auto = lazy(() => import('./pages/Auto/Auto'));
const BrandInfo = lazy(() => import('./pages/Auto/BrandInfo/BrandInfo'));
const ModelInfo = lazy(() => import('./pages/Auto/ModelInfo/ModelInfo'));
const EquipmentInfo = lazy(
  () => import('./pages/Auto/EquipmentInfo/EquipmentInfo')
);

const Reports = lazy(() => import('./pages/Reports/Reports'));
const SystemSettings = lazy(
  () => import('./pages/SystemSettings/SystemSettings')
);

const AdminProvider = () => {
  const { i18n } = useTranslation();
  const location = useLocation();

  const { userInfo, userAccesses } = useAppSelector((state) => state.userInfo);
  const isAdmin = userAccesses.global.isAdmin;

  if (userInfo && !isAdmin && location.pathname.includes(ADMIN_ROUTES.root)) {
    return <Navigate to={`/${i18n.language}/${ROUTES.home}`} replace />;
  }

  if (location.pathname === `/${i18n.language}/${ADMIN_ROUTES.root}`) {
    return (
      <Navigate
        to={`/${i18n.language}/${getFullAdminRoute(ADMIN_ROUTES.users)}`}
        replace
      />
    );
  }

  return (
    <div className="admin-panel">
      <Suspense
        name="admin-panel-suspense"
        fallback={<MainLoader fullScreen />}
      >
        <Outlet />
      </Suspense>
    </div>
  );
};

const AdminRoutes: RouteObject[] = [
  {
    path: ADMIN_ROUTES.root,
    element: <AdminProvider />,
    children: [
      {
        path: ADMIN_ROUTES.users,
        element: <Users />
      },
      {
        path: ADMIN_ROUTES.usersCreate,
        element: <UserInfoPage />
      },
      {
        path: ADMIN_ROUTES.user(),
        element: <UserInfoPage />
      },
      {
        path: ADMIN_ROUTES.positionCreate,
        element: <PositionInfoPage />
      },
      {
        path: ADMIN_ROUTES.position(),
        element: <PositionInfoPage />
      },
      {
        path: ADMIN_ROUTES.rolesActionCreate,
        element: <RulesActionsInfoPage />
      },
      {
        path: ADMIN_ROUTES.rolesAction(),
        element: <RulesActionsInfoPage />
      },

      {
        path: ADMIN_ROUTES.clients,
        element: <Clients />
      },
      {
        path: ADMIN_ROUTES.client(),
        element: <ClientInfoPage />
      },

      {
        path: ADMIN_ROUTES.blackList,
        element: <BlackList />
      },
      {
        path: ADMIN_ROUTES.blackListCreate,
        element: <BlackItem />
      },
      {
        path: ADMIN_ROUTES.blackListItem(),
        element: <BlackItem />
      },

      {
        path: ADMIN_ROUTES.modules,
        element: <Modules />
      },
      {
        path: ADMIN_ROUTES.modulesCreate,
        element: <ModuleInfoPage />
      },
      {
        path: ADMIN_ROUTES.module(),
        element: <ModuleInfoPage />
      },

      {
        path: ADMIN_ROUTES.externalServices,
        element: <ExternalServices />
      },
      {
        path: ADMIN_ROUTES.externalServicesCreate,
        element: <ExternalServiceInfo />
      },
      {
        path: ADMIN_ROUTES.externalService(),
        element: <ExternalServiceInfo />
      },

      {
        path: ADMIN_ROUTES.products,
        element: <Products />
      },
      {
        path: ADMIN_ROUTES.productsCreate,
        element: <ProductInfo />
      },
      {
        path: ADMIN_ROUTES.product(),
        element: <ProductInfo />
      },

      {
        path: ADMIN_ROUTES.partners,
        element: <Partners />
      },
      {
        path: ADMIN_ROUTES.partnersCreate,
        element: <PartnerInfo />
      },
      {
        path: ADMIN_ROUTES.partner(),
        element: <PartnerInfo />
      },
      {
        path: ADMIN_ROUTES.dealersCreate,
        element: <PartnerDealerInfo />
      },
      {
        path: ADMIN_ROUTES.dealer(),
        element: <PartnerDealerInfo />
      },
      {
        path: ADMIN_ROUTES.assessmentCentersCreate,
        element: <AssessmentCenterInfo />
      },
      {
        path: ADMIN_ROUTES.assessmentCenter(),
        element: <AssessmentCenterInfo />
      },

      {
        path: ADMIN_ROUTES.branches,
        element: <Branches />
      },
      {
        path: ADMIN_ROUTES.branchesCreate,
        element: <BranchInfo />
      },
      {
        path: ADMIN_ROUTES.branch(),
        element: <BranchInfo />
      },

      {
        path: ADMIN_ROUTES.auto,
        element: <Auto />
      },
      {
        path: ADMIN_ROUTES.brandsCreate,
        element: <BrandInfo />
      },
      {
        path: ADMIN_ROUTES.brand(),
        element: <BrandInfo />
      },
      {
        path: ADMIN_ROUTES.modelsCreate,
        element: <ModelInfo />
      },
      {
        path: ADMIN_ROUTES.model(),
        element: <ModelInfo />
      },
      {
        path: ADMIN_ROUTES.equipmentsCreate,
        element: <EquipmentInfo />
      },
      {
        path: ADMIN_ROUTES.equipment(),
        element: <EquipmentInfo />
      },
      {
        path: ADMIN_ROUTES.reports,
        element: <Reports />
      },
      {
        path: ADMIN_ROUTES.systemSettings,
        element: <SystemSettings />
      }
    ]
  }
];

export default AdminRoutes;
